import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./button";
import { Label } from "./label";
import { Minus, Plus } from "lucide-react";
import {
  parseTyped,
  releaseTarget,
  secondsDisplay,
  snapToStep,
  stepFrom,
  stripValues,
  tidyTyped,
  valueFromDrag,
} from "../../utils/numberStrip";

const prefersReducedMotion = () => {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  } catch {
    return false;
  }
};

const buzz = () => {
  try {
    navigator.vibrate?.(4);
  } catch {
    /* vibration is a nicety */
  }
};

/**
 * Mobile-friendly numeric stepper: − on the left, + on the right, and a
 * sideways strip of values in the middle that follows the finger.
 *
 * The strip shows the value large in the centre with its neighbours fading
 * out to each side, one step apart in both value and pixels, so dragging
 * moves the numbers themselves and the one under the centre is the one you
 * get. Dragging right slides the smaller values in, as on a wheel picker.
 * With `momentum` (time, where the range is long) a flick keeps gliding. Tap a faded
 * neighbour to jump to it; arrow keys, Page Up/Down and Home/End work when
 * focused. Tap the value at rest (or press Enter) to type one in, for values
 * too far to drag to (#176); a typed value can sit off the step grid (96 on a
 * 2.5 kg stepper), and − / + then step to the grid either side. At rest only
 * the value and its unit show, centred; the strip appears while the value is
 * changing. One row: it replaced the two-row time scale (#97).
 *
 * Props:
 *   label        – field label shown above (optional)
 *   value        – current numeric value
 *   onChange     – (newValue: number) => void
 *   min / max    – range (default 0–999)
 *   step         – increment per step (default 1)
 *   unit         – unit shown beside the value (e.g. "min", "reps"). With
 *                  "s" and no formatValue, a minute or more reads as m:ss min
 *   offLabel     – text shown when value === 0 and min is 0 (default "OFF")
 *   prominent    – taller, larger variant
 *   formatValue  – (v) => string, for the displayed numbers
 *   parseValue   – (typed number) => value, the inverse of formatValue when it
 *                  scales (e.g. km shown, metres stored)
 *   momentum     – glide after a flick, for long ranges such as time
 *   id, ariaLabel – for the focusable strip (aria-label defaults to label)
 */
const DraggableStepper = ({
  label,
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  unit,
  offLabel = "OFF",
  prominent = false,
  formatValue,
  parseValue,
  momentum = false,
  id,
  ariaLabel,
}) => {
  const pxPerStep = prominent ? 56 : 48;
  const opts = { min, max, step, pxPerStep, momentum };

  // While dragging: the continuous value under the centre. After a release
  // or a tap: an offset the strip glides back from.
  const [raw, setRaw] = useState(null);
  const [glide, setGlide] = useState({ offset: 0, ms: 0 });
  // The strip of numbers shows only while the value is changing (dragging, or
  // gliding after a tap); at rest the value and unit sit alone, centred, so
  // the unit never collides with a neighbouring number.
  const [active, setActive] = useState(false);
  const drag = useRef(null);
  const surfaceRef = useRef(null);
  const frame = useRef(null);
  const settleTimer = useRef(null);
  // Typing a value in: the draft text, or null when not editing.
  const [draft, setDraft] = useState(null);
  const inputRef = useRef(null);
  const editing = draft !== null;

  const settleAfter = useCallback((ms) => {
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => setActive(false), ms);
  }, []);

  const isOff = value === 0 && min === 0;
  const inSeconds = unit === "s" && !formatValue;
  const format = (v) => (inSeconds ? secondsDisplay(v).text : (formatValue || String)(v));
  const show = (v) => (v === 0 && min === 0 ? offLabel : format(v));
  const shownUnit = inSeconds ? secondsDisplay(value).unit : unit;

  const commit = useCallback(
    (next) => {
      if (next !== value) {
        onChange(next);
        buzz();
      }
    },
    [onChange, value],
  );

  // Glide from `fromPx` (where the strip is drawn relative to the new value)
  // back to rest, over a time that grows with the distance.
  const glideFrom = useCallback(
    (fromPx) => {
      cancelAnimationFrame(frame.current);
      if (!fromPx || prefersReducedMotion()) {
        setGlide({ offset: 0, ms: 0 });
        settleAfter(fromPx ? 0 : 120);
        return;
      }
      const ms = Math.min(420, Math.max(140, Math.abs(fromPx) * 1.2));
      setActive(true);
      setGlide({ offset: fromPx, ms: 0 });
      frame.current = requestAnimationFrame(() => {
        frame.current = requestAnimationFrame(() => {
          setGlide({ offset: 0, ms });
        });
      });
      // Back to the plain value once the strip has come to rest.
      settleAfter(ms + 220);
    },
    [settleAfter],
  );

  useEffect(
    () => () => {
      cancelAnimationFrame(frame.current);
      clearTimeout(settleTimer.current);
    },
    [],
  );

  const step1 = (direction) => {
    const next = stepFrom(value, direction, opts);
    if (next === value) return;
    commit(next);
    glideFrom(((next - value) / step) * pxPerStep);
  };

  const startEditing = () => {
    cancelAnimationFrame(frame.current);
    clearTimeout(settleTimer.current);
    setActive(false);
    setGlide({ offset: 0, ms: 0 });
    setDraft(isOff ? "" : format(value));
  };

  useEffect(() => {
    if (!editing) return;
    const input = inputRef.current;
    input?.focus();
    input?.select();
  }, [editing]);

  // Escape cancels the typing. Sheets and dialogs close on Escape from a
  // document listener in the capture phase, so catch it earlier, on window,
  // and mark it handled so the sheet stays open.
  const cancelRef = useRef(null);
  cancelRef.current = () => finishEditing(false);
  useEffect(() => {
    if (!editing) return undefined;
    const onEscape = (e) => {
      if (e.key !== "Escape" || e.target !== inputRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      cancelRef.current();
    };
    window.addEventListener("keydown", onEscape, true);
    return () => window.removeEventListener("keydown", onEscape, true);
  }, [editing]);

  const finishEditing = (save) => {
    // Enter closes the box, and its blur on the way out must not run again.
    if (!inputRef.current || inputRef.current.dataset.done) return;
    inputRef.current.dataset.done = "1";
    if (save) {
      const typed = parseTyped(draft, { inSeconds, shownUnit, parseValue });
      // Typed values aren't snapped to the step: 96 kg stays 96.
      if (typed !== null) commit(tidyTyped(typed, opts));
    }
    setDraft(null);
    // Back to the slider so arrow keys keep working.
    requestAnimationFrame(() => surfaceRef.current?.focus({ preventScroll: true }));
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const wasActive = active;
    cancelAnimationFrame(frame.current);
    clearTimeout(settleTimer.current);
    setGlide({ offset: 0, ms: 0 });
    setActive(true);
    drag.current = {
      x: e.clientX,
      start: value,
      moved: false,
      wasActive,
      samples: [{ x: e.clientX, t: e.timeStamp }],
      pointerId: e.pointerId,
    };
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* capture is best-effort */
    }
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 4) d.moved = true;
    d.samples = [...d.samples.slice(-4), { x: e.clientX, t: e.timeStamp }];
    const continuous = valueFromDrag(d.start, dx, opts);
    setRaw(continuous);
    commit(snapToStep(continuous, opts));
  };

  const endDrag = (e) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    try {
      if (surfaceRef.current?.hasPointerCapture?.(d.pointerId)) {
        surfaceRef.current.releasePointerCapture(d.pointerId);
      }
    } catch {
      /* already released */
    }

    if (!d.moved) {
      setRaw(null);
      // A tap at rest, when only the value shows, opens it for typing.
      if (!d.wasActive && e?.type !== "pointercancel") {
        startEditing();
        return;
      }
      // While the strip shows, a tap on a faded neighbour jumps to it.
      const box = surfaceRef.current?.getBoundingClientRect();
      if (box && e?.clientX != null) {
        const k = Math.round((e.clientX - (box.left + box.width / 2)) / pxPerStep);
        if (k !== 0) {
          const next = snapToStep(value + k * step, opts);
          commit(next);
          glideFrom(((next - value) / step) * pxPerStep);
          return;
        }
      }
      settleAfter(120);
      return;
    }

    const first = d.samples[0];
    const last = d.samples[d.samples.length - 1];
    const elapsed = Math.max(1, last.t - first.t);
    const velocity = e?.type === "pointercancel" ? 0 : (last.x - first.x) / elapsed;
    const continuous = valueFromDrag(d.start, (e?.clientX ?? last.x) - d.x, opts);
    const target = releaseTarget(continuous, velocity, opts);
    setRaw(null);
    commit(target);
    glideFrom(((target - continuous) / step) * pxPerStep);
  };

  const onKeyDown = (e) => {
    const arrows = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1 };
    const pages = { PageDown: -step * 5, PageUp: step * 5 };
    let next = null;
    if (e.key in arrows) next = stepFrom(value, arrows[e.key], opts);
    else if (e.key in pages) next = value + pages[e.key];
    else if (e.key === "Home") next = min;
    else if (e.key === "End") next = max;
    else if (e.key === "Enter") {
      e.preventDefault();
      startEditing();
      return;
    }
    if (next === null) return;
    e.preventDefault();
    const snapped = snapToStep(next, opts);
    if (snapped !== value) {
      commit(snapped);
      glideFrom(((snapped - value) / step) * pxPerStep);
    }
  };

  // Where the strip sits relative to `value`: tracking the finger while
  // dragging, otherwise gliding back to rest.
  const offset = raw !== null ? ((value - raw) / step) * pxPerStep : glide.offset;
  const values = stripValues(value, { min, max, step, reach: 4 });

  const height = prominent ? "h-12" : "h-10";
  const sideButton = `rounded-none hover:bg-primary/20 hover:text-primary focus:ring-1 focus:ring-primary/50 transition-colors shrink-0 ${
    prominent ? "h-12 w-12" : "h-10 w-10"
  }`;

  return (
    <div className="flex flex-col gap-1.5 w-full min-w-0">
      {label && (
        <Label
          htmlFor={id}
          className={`text-muted-foreground uppercase tracking-widest font-semibold text-center truncate ${
            prominent ? "text-xs text-primary/80" : "text-[10px]"
          }`}
        >
          {label}
        </Label>
      )}
      <div
        className={`flex items-center justify-between bg-background/80 border rounded-lg shadow-sm overflow-hidden w-full min-w-0 transition-colors ${
          prominent ? "border-primary/40 shadow-[0_0_10px_rgba(var(--primary),0.1)]" : "border-border/50"
        } ${height}`}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={sideButton}
          aria-label={`${step} ${unit || ""} less`.replace(/\s+/g, " ").trim()}
          disabled={value <= min}
          onClick={() => step1(-1)}
        >
          <Minus className={prominent ? "h-5 w-5" : "h-4 w-4"} />
        </Button>

        <div
          ref={surfaceRef}
          id={id}
          role="slider"
          tabIndex={0}
          aria-label={ariaLabel || label}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={
            isOff
              ? offLabel
              : inSeconds
                ? secondsDisplay(value).spoken
                : `${format(value)}${unit ? ` ${unit}` : ""}`
          }
          className={`relative flex-1 h-full min-w-0 overflow-hidden border-x border-border/30 cursor-ew-resize select-none outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60 [touch-action:pan-y] ${
            isOff ? "bg-muted/5" : prominent ? "bg-primary/5" : "bg-muted/10"
          }`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
          title="Drag sideways, or tap to type a number"
        >
          {editing && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center gap-1.5 bg-background px-2"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                enterKeyHint="done"
                autoComplete="off"
                aria-label={`Type ${ariaLabel || label || "a value"}${shownUnit ? ` in ${shownUnit}` : ""}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => finishEditing(true)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                    finishEditing(true);
                  }
                }}
                className={`w-full min-w-0 bg-transparent border-b border-primary/60 text-center font-bold leading-none tabular-nums text-foreground outline-none ${
                  prominent ? "text-lg" : "text-sm"
                }`}
                style={{ maxWidth: "6ch" }}
              />
              {shownUnit && (
                <span
                  className={`font-medium uppercase tracking-widest text-muted-foreground leading-none truncate ${
                    prominent ? "text-[10px]" : "text-[9px]"
                  }`}
                >
                  {shownUnit}
                </span>
              )}
            </div>
          )}
          {/* At rest: the value and its unit, centred. */}
          <div
            className={`pointer-events-none absolute inset-0 flex items-center justify-center gap-1.5 transition-opacity duration-150 ${
              active || editing ? "opacity-0" : "opacity-100"
            }`}
            aria-hidden="true"
          >
            <span
              className={`font-bold leading-none tabular-nums ${
                isOff
                  ? "text-muted-foreground text-sm"
                  : `text-foreground ${prominent ? "text-lg" : "text-sm"}`
              }`}
            >
              {show(value)}
            </span>
            {!isOff && shownUnit && (
              <span
                className={`font-medium uppercase tracking-widest text-muted-foreground leading-none truncate ${
                  prominent ? "text-[10px]" : "text-[9px]"
                }`}
              >
                {shownUnit}
              </span>
            )}
          </div>

          {/* While changing: the strip of numbers, following the finger. The
              fade is on the numbers only, so the cell's background stays even. */}
          <div
            className={`pointer-events-none absolute inset-0 transition-opacity duration-150 [mask-image:linear-gradient(90deg,transparent_4%,#000_32%,#000_68%,transparent_96%)] ${
              active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden="true"
          >
            <div
              className="absolute inset-0"
              style={{
                transform: `translateX(${offset}px)`,
                transition: glide.ms ? `transform ${glide.ms}ms cubic-bezier(0.2, 0.8, 0.2, 1)` : "none",
              }}
            >
              {values.map(({ value: v, offset: k }) => {
                const centre = k === 0;
                return (
                  <span
                    key={v}
                    className={`absolute top-1/2 font-bold leading-none whitespace-nowrap tabular-nums ${
                      centre
                        ? `text-foreground ${prominent ? "text-lg" : "text-sm"}`
                        : `text-muted-foreground ${prominent ? "text-sm" : "text-xs"}`
                    }`}
                    style={{
                      left: `calc(50% + ${k * pxPerStep}px)`,
                      transform: "translate(-50%, -50%)",
                      opacity: centre ? 1 : Math.max(0.2, 0.6 - (Math.abs(k) - 1) * 0.2),
                    }}
                  >
                    {show(v)}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={sideButton}
          aria-label={`${step} ${unit || ""} more`.replace(/\s+/g, " ").trim()}
          disabled={value >= max}
          onClick={() => step1(1)}
        >
          <Plus className={prominent ? "h-5 w-5" : "h-4 w-4"} />
        </Button>
      </div>
    </div>
  );
};

export default DraggableStepper;
